// How a save-intent context's gallery is settled: the three gallery fields
// travel as a unit, and the Redux gallery (the one the parent entered this
// ordering flow with) fills them in when the context names none. Shared by
// useImageIntentFriction (per-image reports) and useImageIntentSaveShortcut
// (the Cmd/Ctrl+S registration), so both credit a gesture the same way.
import type { AdditionalDataState } from '../../../../reducers/additionalDataSlice';
import type { ImageIntentContext } from '../../../../utils/imageIntentReporter';
import { imageIntentGalleryFromAdditionalData } from './imageIntentGallery';

export type GalleryAttribution = Required<
  Pick<ImageIntentContext, 'galleryKey' | 'picsGalleryId' | 'dp2GalleryId'>
>;

export interface StateWithAdditionalData {
  additionalData?: Partial<AdditionalDataState> | null;
}

// The gallery the parent entered this ordering flow with (one definition,
// shared with cart snapshots and the SweetAlert dialogs).
export const selectGalleryDefaults = (
  state: StateWithAdditionalData,
): GalleryAttribution =>
  imageIntentGalleryFromAdditionalData(state.additionalData ?? null);

// A context that sets any of the three fields (even to null, meaning "no
// gallery") knows its image's gallery, so its three are used as-is; otherwise
// the Redux gallery fills all three. Mixing the two could pair one gallery's
// key with another gallery's id.
export const resolveGallery = (
  context: ImageIntentContext,
  defaults: GalleryAttribution,
): GalleryAttribution => {
  const explicit =
    context.galleryKey !== undefined ||
    context.picsGalleryId !== undefined ||
    context.dp2GalleryId !== undefined;
  if (!explicit) return defaults;

  return {
    galleryKey: context.galleryKey ?? null,
    picsGalleryId: context.picsGalleryId ?? null,
    dp2GalleryId: context.dp2GalleryId ?? null,
  };
};
